"use client";

import { useCallback } from "react";
import { useRouter } from "next/navigation";
import { Sparkles } from "lucide-react";

export default function IncidentCard({
  date,
  title,
  preview,
  metadata = [],
  href,
  actionMenu = null,
}) {
  const router = useRouter();

  const openIncident = useCallback(() => {
    if (href) router.push(href);
  }, [href, router]);

  const handleCardKeyDown = useCallback(
    (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      openIncident();
    },
    [openIncident]
  );

  return (
    <article
      role="link"
      tabIndex={0}
      aria-label={`Open ${title}`}
      onClick={openIncident}
      onKeyDown={handleCardKeyDown}
      className="cursor-pointer rounded-[var(--radius)] border border-[var(--border)] bg-[var(--card)] px-4 py-5 shadow-[var(--shadow-e1)] transition-transform hover:translate-y-[-1px] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] sm:px-6"
    >
      <div className="flex items-start gap-3 sm:gap-4">
        <div className="shrink-0 text-left">
          <div
            className="text-[var(--muted)]"
            style={{ fontSize: "11px", lineHeight: "14px", letterSpacing: "0.18em", textTransform: "uppercase" }}
          >
            {date?.year}
          </div>
          <div className="mt-1 text-[var(--fg-primary)]" style={{ fontSize: "22px", lineHeight: "26px", fontWeight: 600 }}>
            {date?.monthDay}
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--muted)]">
              <Sparkles className="h-3.5 w-3.5" strokeWidth={1.8} />
              <span>Incident</span>
            </div>
            {actionMenu ? <div onClick={(event) => event.stopPropagation()}>{actionMenu}</div> : null}
          </div>
          <h3 className="mt-3 text-[var(--fg-primary)]" style={{ fontSize: "18px", lineHeight: "26px", fontWeight: 600 }}>
            {title}
          </h3>
          <div className="mt-2.5 max-w-2xl text-[var(--fg-secondary)]" style={{ fontSize: "15px", lineHeight: "24px" }}>
            {preview}
          </div>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-[var(--border)] pt-4">
        <div className="flex flex-wrap items-center gap-4 text-sm text-[var(--muted)]">
          {metadata.map((item, index) => {
            const Icon = item.icon;
            return (
              <span key={`${item.label}-${index}`} className="inline-flex items-center gap-1.5">
                {Icon ? <Icon className="h-4 w-4" strokeWidth={1.8} /> : null}
                {item.label}
              </span>
            );
          })}
        </div>
      </div>
    </article>
  );
}
